import { type MouseEvent, type ReactNode, useState } from 'react';
import { Spinner } from '@cmsgov/design-system';
import {
  getPdtEditLinkAriaLabelSuffix,
  getPdtListAlertVariant,
  type PdtScanStatus,
} from '@access-cms/shared-constants';
import {
  useCreateLaborCategory,
  useDeleteLaborCategory,
  useLaborCategoriesQuery,
  useUpdateLaborCategory,
} from '@access-cms/api-client';

import { DetailItem, DetailsList } from '../../components/DetailsList';
import { ErrorIcon, WarningIcon } from '../../components/Icon';
import { Drawer, DrawerLink } from '../../components/Drawer';
import { HelpDrawerLink } from '../../components/HelpDrawerLink';
import { RequiredFieldsLegend } from '../../components/RequiredFieldsLegend';
import { useAlerts, useModal } from '../../hooks';
import { useContractData } from './context';
import { ContractFormActions, DeleteLaborCategoryModal, PdtFreshnessNotice } from './components';
import { LaborCategoryData, LaborCategoryDrawer, LaborCategoryModalMode } from './components/LaborCategoryModal';
import {
  getLaborCategoryPdtUploadErrorMessage,
  getLaborCategorySaveErrorMessage,
  hasActiveLaborCategoryNameConflict,
  LABOR_CATEGORY_DUPLICATE_NAME_MESSAGE,
} from './utils/labor-category-errors';
import { getLaborCategoryListRefetchInterval } from './utils/labor-category-list-refetch';
import { EXISTING_PDT_FILE_ID, uploadLaborCategoryPdtAfterSave } from './utils/labor-category-pdt';

import { formatDate } from '@/utils';
import { CONTRACT_ROLE, useRBAC } from '@/hooks/useRBAC';

function formatPdtUploadSummary(pdtUploadedAt?: string | null): string {
  const uploadedLabel = pdtUploadedAt ? formatDate(pdtUploadedAt) : '-';

  return `PDT uploaded on ${uploadedLabel}`;
}

const LABOR_CATEGORY_DELETE_DISABLED_REASON = 'This Labor Category has active users and cannot be deleted';

function pdtFreshnessLinkIcon(
  pdtScanStatus?: PdtScanStatus | string | null,
  pdtUploadedAt?: string | null,
): ReactNode | undefined {
  const alertVariant = getPdtListAlertVariant(pdtScanStatus, pdtUploadedAt);

  if (alertVariant === 'warning') {
    return (
      <WarningIcon
        ariaLabel=''
        size={18}
      />
    );
  }

  if (alertVariant === 'error') {
    return (
      <ErrorIcon
        ariaLabel=''
        size={18}
      />
    );
  }

  return undefined;
}

function pdtFreshnessEditAriaLabel(
  categoryName: string,
  pdtScanStatus?: PdtScanStatus | string | null,
  pdtUploadedAt?: string | null,
): string {
  const suffix = getPdtEditLinkAriaLabelSuffix(pdtScanStatus, pdtUploadedAt);

  if (suffix) {
    return `Edit ${categoryName}, ${suffix}`;
  }

  return `Edit ${categoryName}`;
}

const RISK_LEVEL_MAP = {
  T1: { apiKey: 'T1', displayName: 'T1' },
  T2: { apiKey: 'T2', displayName: 'T2' },
  T4: { apiKey: 'T4', displayName: 'T4' },
} as const;

type UiRiskLevelValue = keyof typeof RISK_LEVEL_MAP;
type ApiLaborCategoryRiskLevel = (typeof RISK_LEVEL_MAP)[UiRiskLevelValue]['apiKey'];

// Create a unique derived name from the category name. We could considre doing this
// in the API instead.
function laborCategoryCodeFromName(name: string): string {
  const trimmed = name.trim();
  const fromName = trimmed
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
  if (fromName.length > 0) {
    return fromName;
  }

  return `LC-${globalThis.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()}`;
}

function validatePdtFileRequired(
  data: LaborCategoryData,
  mode: LaborCategoryModalMode,
  onError: (title: string, message: string) => void,
): boolean {
  const uploadedFile = data.files[0];
  console.info('[PDT] validatePdtFileRequired', {
    fileCount: data.files.length,
    fileId: uploadedFile?.id,
    fileName: uploadedFile?.name,
    fileNameLength: uploadedFile?.name?.length,
    fileSize: uploadedFile?.file?.size,
    hasFileObject: !!uploadedFile?.file,
    mode,
    timestamp: new Date().toISOString(),
  });
  if (!uploadedFile?.name?.trim()) {
    onError(
      mode === LaborCategoryModalMode.ADD ? 'Could not add Labor Category' : 'Could not update Labor Category',
      'A Position Designation Tool (PDT) file is required.',
    );

    return false;
  }

  if (mode === LaborCategoryModalMode.ADD && (!uploadedFile.file || uploadedFile.file.size === 0)) {
    onError(
      'Could not add Labor Category',
      'The uploaded file could not be read or is empty. Please remove and re-upload.',
    );

    return false;
  }

  if (mode === LaborCategoryModalMode.EDIT) {
    const isExistingPdt =
      uploadedFile.id === EXISTING_PDT_FILE_ID && (!uploadedFile.file || uploadedFile.file.size === 0);
    const isNewUpload = !!uploadedFile.file && uploadedFile.file.size > 0;

    if (!isExistingPdt && !isNewUpload) {
      onError(
        'Could not update Labor Category',
        'The uploaded file could not be read or is empty. Please remove and re-upload.',
      );

      return false;
    }
  }

  return true;
}

function LaborCategoriesTab() {
  const { contract } = useContractData();
  const { errorAlert, successAlert } = useAlerts();
  const { closeModal, openModal } = useModal();

  const { hasRoleOnContract, isAdmin } = useRBAC();
  const canManageLaborCategories = isAdmin || hasRoleOnContract(contract?.id ?? '', CONTRACT_ROLE.COR);

  const laborCategoriesQuery = useLaborCategoriesQuery(contract?.id ?? '', {
    enabled: !!contract?.id,
    isActive: true,
    refetchInterval: (query) => getLaborCategoryListRefetchInterval(query.state.data?.data ?? []),
  });
  const laborCategories = laborCategoriesQuery.data?.data ?? [];

  const createLaborCategory = useCreateLaborCategory(contract?.id ?? '');
  const updateLaborCategory = useUpdateLaborCategory(contract?.id ?? '');
  const deleteLaborCategory = useDeleteLaborCategory(contract?.id ?? '');

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isLaborCategoryDrawerOpen, setIsLaborCategoryDrawerOpen] = useState(false);
  const [isLaborCategorySubmitting, setIsLaborCategorySubmitting] = useState(false);
  const [drawerMode, setDrawerMode] = useState<LaborCategoryModalMode>(LaborCategoryModalMode.ADD);
  const [selectedLaborCategoryId, setSelectedLaborCategoryId] = useState<string | undefined>(undefined);
  const baseTestId = 'contract-labor-categories-tab';

  if (!contract) {
    return (
      <div
        className='contract-labor-categories-tab-content'
        data-testid={baseTestId}>
        <p
          className='ds-text'
          data-testid={`${baseTestId}-empty`}>
          No contract information available.
        </p>
      </div>
    );
  }

  if (laborCategoriesQuery.isLoading) {
    return (
      <div
        className='contract-settings-tab-content ds-u-justify-content--center'
        data-testid={baseTestId}>
        <div data-testid={`${baseTestId}-loading`}>
          <Spinner aria-valuetext='Loading labor categories...' />
        </div>
      </div>
    );
  }

  if (laborCategoriesQuery.isError) {
    return (
      <div
        className='contract-settings-tab-content'
        data-testid={baseTestId}>
        <p
          className='ds-text'
          data-testid={`${baseTestId}-error`}>
          Failed to load labor categories.
        </p>
      </div>
    );
  }

  const dismissLaborCategoryDrawer = () => {
    setIsLaborCategoryDrawerOpen(false);
    setSelectedLaborCategoryId(undefined);
  };

  const handleLaborCategoryMutationError = (
    error: unknown,
    action: 'add' | 'update',
    categoryName: string,
    riskLevelDisplayName: string,
  ) => {
    setIsLaborCategorySubmitting(false);
    errorAlert({
      message: getLaborCategorySaveErrorMessage(error, {
        action,
        categoryName,
        riskLevelDisplayName,
      }),
      title: action === 'add' ? 'Could not add Labor Category' : 'Could not update Labor Category',
    });
    dismissLaborCategoryDrawer();
  };

  const handleLaborCategorySaveSuccess = async (
    laborCategoryId: string,
    data: LaborCategoryData,
    successMessage: { dismissInSeconds: number; message: string; title: string },
  ) => {
    try {
      await uploadLaborCategoryPdtAfterSave(contract.id ?? '', laborCategoryId, data);
      await laborCategoriesQuery.refetch();
      successAlert(successMessage);
      dismissLaborCategoryDrawer();
    } catch (error) {
      console.error('[PDT] Labor category saved but PDT upload failed', {
        contractId: contract.id,
        error: error instanceof Error ? error.message : String(error),
        laborCategoryId,
      });
      await laborCategoriesQuery.refetch();
      const categoryName = data.name.trim();
      const riskLevelDisplayName = RISK_LEVEL_MAP[data.riskLevel as UiRiskLevelValue]?.displayName ?? data.riskLevel;
      errorAlert({
        message: getLaborCategoryPdtUploadErrorMessage({ categoryName, error, riskLevelDisplayName }),
        title: 'PDT upload failed',
      });
      dismissLaborCategoryDrawer();
    } finally {
      setIsLaborCategorySubmitting(false);
    }
  };

  const handleLaborCategorySave = (data: LaborCategoryData) => {
    const categoryName = data.name.trim();
    const riskLevel = data.riskLevel;
    const riskLevelDisplayName = RISK_LEVEL_MAP[riskLevel as UiRiskLevelValue]?.displayName ?? riskLevel;
    const apiRiskLevel = RISK_LEVEL_MAP[riskLevel as UiRiskLevelValue]?.apiKey as ApiLaborCategoryRiskLevel | undefined;

    if (!validatePdtFileRequired(data, drawerMode, (title, message) => errorAlert({ message, title }))) {
      return;
    }

    const excludeId = drawerMode === LaborCategoryModalMode.EDIT ? selectedLaborCategoryId : undefined;
    if (hasActiveLaborCategoryNameConflict(categoryName, laborCategories, excludeId)) {
      errorAlert({
        message: LABOR_CATEGORY_DUPLICATE_NAME_MESSAGE,
        title:
          drawerMode === LaborCategoryModalMode.ADD
            ? 'Could not add Labor Category'
            : 'Could not update Labor Category',
      });

      return;
    }

    setIsLaborCategorySubmitting(true);

    if (drawerMode === LaborCategoryModalMode.ADD) {
      createLaborCategory.mutate(
        {
          categoryCode: laborCategoryCodeFromName(data.name),
          categoryName,
          displayOrder: nextDisplayOrder,
          riskLevel: apiRiskLevel,
        },
        {
          onError: (error) => {
            handleLaborCategoryMutationError(error, 'add', categoryName, riskLevelDisplayName);
          },
          onSuccess: async (response) => {
            const laborCategoryId = response.data?.id;
            if (!laborCategoryId) {
              setIsLaborCategorySubmitting(false);
              errorAlert({
                message: `${categoryName}, ${riskLevelDisplayName} was created but the labor category id was missing.`,
                title: 'Could not upload PDT',
              });
              dismissLaborCategoryDrawer();

              return;
            }

            await handleLaborCategorySaveSuccess(laborCategoryId, data, {
              dismissInSeconds: 30,
              message: `${categoryName}, ${riskLevelDisplayName} has been added.`,
              title: 'Labor Category added successfully',
            });
          },
        },
      );
    } else {
      const laborCategoryId = selectedLaborCategoryId;
      if (!laborCategoryId) {
        errorAlert({
          message: 'No labor category selected for update.',
          title: 'Could not update Labor Category',
        });

        return;
      }

      updateLaborCategory.mutate(
        {
          body: {
            categoryName,
            displayOrder: nextDisplayOrder,
            riskLevel: apiRiskLevel,
          },
          pathParams: { id: laborCategoryId },
        },
        {
          onError: (error) => {
            handleLaborCategoryMutationError(error, 'update', categoryName, riskLevelDisplayName);
          },
          onSuccess: async () => {
            await handleLaborCategorySaveSuccess(laborCategoryId, data, {
              dismissInSeconds: 10,
              message: `${categoryName}, ${riskLevelDisplayName} has been updated.`,
              title: 'Labor Category Updated Successfully',
            });
          },
        },
      );
    }
  };

  const confirmDeleteLaborCategory = (laborCategoryId: string, categoryName: string, riskLevelDisplayName: string) => {
    closeModal();

    deleteLaborCategory.mutate(
      { id: laborCategoryId },
      {
        onError: (error) => {
          errorAlert({
            message: `${categoryName}, ${riskLevelDisplayName}  could not be deleted: ${error.message}`,
            title: 'Could not delete Labor Category',
          });
        },
        onSuccess: () => {
          successAlert({
            dismissInSeconds: 10,
            message: `${categoryName}, ${riskLevelDisplayName} has been removed.`,
            title: 'Labor Category deleted successfully',
          });
        },
      },
    );
  };

  const openDeleteLaborCategoryModal = (
    laborCategoryId: string,
    categoryName: string,
    riskLevelDisplayName: string,
  ) => {
    openModal(
      <DeleteLaborCategoryModal
        categoryName={categoryName}
        onCancel={closeModal}
        onConfirm={() => confirmDeleteLaborCategory(laborCategoryId, categoryName, riskLevelDisplayName)}
      />,
      'Delete Labor Category?',
    );
  };

  const openAddLaborCategoryDrawer = () => {
    setDrawerMode(LaborCategoryModalMode.ADD);
    setSelectedLaborCategoryId(undefined);
    setIsLaborCategoryDrawerOpen(true);
  };

  const openEditLaborCategoryDrawer = (laborCategoryId: string) => {
    setDrawerMode(LaborCategoryModalMode.EDIT);
    setSelectedLaborCategoryId(laborCategoryId);
    setIsLaborCategoryDrawerOpen(true);
  };

  const closeLaborCategoryDrawer = () => {
    if (isLaborCategorySubmitting) {
      return;
    }

    setIsLaborCategoryDrawerOpen(false);
    setSelectedLaborCategoryId(undefined);
  };

  // Calculate the next display order for a new labor category. This ensures that new
  // categories are added to the end of the list
  const nextDisplayOrder =
    laborCategories.length === 0 ? 0 : Math.max(...laborCategories.map((c) => c.displayOrder ?? 0), 0) + 1;

  const riskLevelOptions = (Object.keys(RISK_LEVEL_MAP) as UiRiskLevelValue[]).map((riskLevelValue) => ({
    label: RISK_LEVEL_MAP[riskLevelValue].displayName,
    value: riskLevelValue,
  }));

  const laborCategoriesItems: DetailItem[] = [...laborCategories]
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map((category) => {
      const categoryName = category.categoryName || 'Unnamed Category';
      const categoryId = category.id ?? '';
      const riskLevelDisplayName =
        RISK_LEVEL_MAP[category.riskLevel as UiRiskLevelValue]?.displayName ?? category.riskLevel;

      return {
        label: categoryName,
        ...(canManageLaborCategories && {
          links: [
            {
              ariaLabel: pdtFreshnessEditAriaLabel(categoryName, category.pdtScanStatus, category.pdtUploadedAt),
              icon: pdtFreshnessLinkIcon(category.pdtScanStatus, category.pdtUploadedAt),
              label: 'Edit',
              onClick: (event: MouseEvent) => {
                event.preventDefault();
                openEditLaborCategoryDrawer(categoryId);
              },
            },
            {
              ariaLabel: `Delete ${categoryName}`,
              disabled: Boolean(category.hasActiveUsers),
              label: 'Delete',
              onClick: (event: MouseEvent) => {
                event.preventDefault();
                if (category.hasActiveUsers) {
                  return;
                }

                openDeleteLaborCategoryModal(categoryId, categoryName, riskLevelDisplayName);
              },
              title: category.hasActiveUsers ? LABOR_CATEGORY_DELETE_DISABLED_REASON : undefined,
            },
          ],
        }),
        value: (
          <>
            <div>
              Risk level:{' '}
              {category.riskLevel
                ? RISK_LEVEL_MAP[category.riskLevel as UiRiskLevelValue]?.displayName ?? category.riskLevel
                : '-'}
            </div>
            <div>{formatPdtUploadSummary(category.pdtUploadedAt)}</div>
            <PdtFreshnessNotice
              pdtScanStatus={category.pdtScanStatus}
              pdtUploadedAt={category.pdtUploadedAt}
            />
          </>
        ),
      };
    });

  return (
    <div
      className='contract-settings-tab-content'
      data-testid={baseTestId}>
      <div
        className='contract-settings-content-container'
        data-testid={`${baseTestId}-content`}>
        <p
          className='ds-u-margin-bottom--0'
          data-testid={`${baseTestId}-intro`}>
          If any of the information in this section is incorrect, please work with your Contracting Officer to have it
          updated. Only Labor Categories with no active users and no in-progress applications may be deleted.
        </p>
        {laborCategoriesItems.length === 0 && <RequiredFieldsLegend data-testid={`${baseTestId}-required-fields`} />}

        <DetailsList
          data-testid={`${baseTestId}-labor-categories`}
          header='Labor Categories'
          headerHint={laborCategoriesItems.length === 0 ? 'You must have at least one Labor Category.' : undefined}
          headerHintIcon={laborCategoriesItems.length === 0 ? 'error' : undefined}
          items={laborCategoriesItems}
          required={laborCategoriesItems.length === 0 ? true : false}
        />

        {canManageLaborCategories && (
          <DrawerLink
            className='ds-u-margin-top--2'
            data-testid={`${baseTestId}-add-labor-category-link`}
            text='Add Labor Category'
            onClick={openAddLaborCategoryDrawer}
          />
        )}

        <HelpDrawerLink
          className='ds-u-margin-top--2'
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpOpen(true)}
        />

        <LaborCategoryDrawer
          contractId={contract.id ?? ''}
          contractNumber={contract.contractNumber ?? ''}
          data-testid={`${baseTestId}-labor-category-drawer`}
          heading={drawerMode === LaborCategoryModalMode.ADD ? 'Add a Labor Category' : 'Edit a Labor Category'}
          isOpen={isLaborCategoryDrawerOpen}
          isSubmitting={isLaborCategorySubmitting}
          laborCategoryId={selectedLaborCategoryId}
          mode={drawerMode}
          riskLevelOptions={riskLevelOptions}
          onClose={closeLaborCategoryDrawer}
          onGetHelp={() => setIsHelpOpen(true)}
          onSave={handleLaborCategorySave}
        />

        <Drawer
          data-testid={`${baseTestId}-drawer`}
          heading='Help: Labor Categories'
          isOpen={isHelpOpen}
          width='50%'
          onClose={() => setIsHelpOpen(false)}>
          {canManageLaborCategories ? (
            <>
              <h3>Labor Categories</h3>
              <p>
                Use this tab to add a list of all possible labor categories the users of this Contract may need. For
                each labor category, you will need to include:
              </p>
              <ul>
                <li>A name or label for the labor category</li>
                <li>Select a risk level that a user would need approval for</li>
                <li>Include a signed Position Designation Tool (PDT).</li>
              </ul>
              <p>
                The PDT for each labor category must be updated with a new signed copy each year. A warning icon will
                appear when one is nearing or past the due date. If a PDT is not updated within a year, that labor
                category will expire and cannot be assigned to users. PDTs can be downloaded from the{' '}
                <a
                  href='https://www.dcsa.mil/Systems-Applications/National-Background-Investigation-Services-NBIS/'
                  rel='noreferrer'
                  target='_blank'>
                  National Background Investigation Services
                </a>{' '}
                site.
              </p>
              <p>
                If a labor category is no longer needed and is not currently assigned to any users, it can be deleted
                from this list. If a labor category is currently assigned to a user, it cannot be deleted.
              </p>
            </>
          ) : (
            <>
              <h3>Labor Categories</h3>
              <p>
                Labor Categories (LCATs) are job titles. Each one has a risk level that needs a certain level of
                approval. This is a list of the job titles available for this Contract. Choose from this list when
                assigning Labor Categories to users on this Contract.
              </p>
              <p>
                A warning icon will appear when one has expired and can no longer be assigned to users. Contact the
                Federal Approver if this warning icon appears. They will need to update the Position Designation Tool
                (PDT) to fix it. Any users who are already assigned to a Labor Category before it expires will still be
                able to keep their Labor Category and will not need a new one.
              </p>
            </>
          )}
        </Drawer>
        <ContractFormActions
          data-testid={`${baseTestId}-actions`}
          isEditable={false}
        />
      </div>
    </div>
  );
}

export default LaborCategoriesTab;
